import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Upload, RefreshCw, Trash2, FileText, XCircle, CheckCircle2, Search } from 'lucide-react';
import { adminAPI, fmtError } from '@/lib/api';

const PRODUCT_TYPES = ['one_step', 'instant', 'funded'];
const SIZES = [5000, 10000, 20000];
const STATUSES = ['AVAILABLE', 'RESERVED', 'ASSIGNED', 'ACTIVE', 'RETIRED', 'BLOCKED'];

const TYPE_LABEL = { one_step: 'One-Step', instant: 'Instant', funded: 'Funded' };
const STATUS_TINT = {
  AVAILABLE: 'text-[#00ff66]', RESERVED: 'text-amber-400', ASSIGNED: 'text-sky-400',
  ACTIVE: 'text-white', RETIRED: 'text-zinc-500', BLOCKED: 'text-[#ff3366]',
};
const REQUIRED_COLS = ['product_type', 'account_size', 'mt_login', 'mt_password', 'mt_server'];
const OPTIONAL_COLS = ['mt_investor_password', 'mt_broker'];

export default function AdminInventory() {
  const [inv, setInv] = useState([]);
  const [products, setProducts] = useState([]);
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [importOpen, setImportOpen] = useState(false);

  const [filter, setFilter] = useState({ product_type: '', account_size: '', status: '' });
  const [q, setQ] = useState('');

  const load = async () => {
    try {
      setErr('');
      const [invRes, prods, poolRes] = await Promise.all([
        adminAPI.poolInventory(),
        adminAPI.products(),
        adminAPI.poolList(),
      ]);
      setInv(invRes || []);
      setProducts(prods || []);
      setRows(poolRes || []);
    } catch (e) { setErr(fmtError(e)); }
  };
  useEffect(() => { load(); }, []);

  // ---------- Matrix helpers ----------
  const cell = (pt, sz, st) =>
    (inv.find(r => r.product_type === pt && r.account_size === sz && r.status === st) || {}).count || 0;
  const rowTotal = (pt, sz) => STATUSES.reduce((t, st) => t + cell(pt, sz, st), 0);
  const isLowStock = (pt, sz) => {
    const p = products.find(x => x.product_type === pt && x.account_size === sz);
    if (!p || !p.active) return false; // only flag active products
    return cell(pt, sz, 'AVAILABLE') === 0;
  };
  const lowStockLines = useMemo(() => {
    const out = [];
    for (const pt of ['one_step', 'instant']) for (const sz of SIZES) {
      if (isLowStock(pt, sz)) out.push(`${TYPE_LABEL[pt]} $${sz.toLocaleString()}`);
    }
    return out;
  }, [inv, products]);

  // ---------- Pool table filter ----------
  const filtered = useMemo(() => {
    let out = rows;
    if (filter.product_type) out = out.filter(r => r.product_type === filter.product_type);
    if (filter.account_size) out = out.filter(r => String(r.account_size) === filter.account_size);
    if (filter.status) out = out.filter(r => r.status === filter.status);
    if (q.trim()) {
      const needle = q.trim().toLowerCase();
      out = out.filter(r => (r.mt_login || '').toLowerCase().includes(needle)
                          || (r.mt_server || '').toLowerCase().includes(needle));
    }
    return out;
  }, [rows, filter, q]);

  const retire = async (poolId) => {
    if (!window.confirm('Retire this pool account? This will remove it from the AVAILABLE pool.')) return;
    setBusy(true);
    try { await adminAPI.poolRetire(poolId); await load(); }
    catch (e) { setErr(fmtError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="admin-inventory-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="label-sm mb-2">Account inventory</div>
          <h1 className="font-display text-3xl">Pool Inventory</h1>
          <p className="text-xs text-zinc-500 mt-1">Pre-provisioned MT5 credentials organized by product × size.</p>
        </div>
        <div className="flex gap-2">
          <button className="btn-ghost btn-sm" onClick={load} disabled={busy} data-testid="inv-refresh">
            <RefreshCw size={13} /> Refresh
          </button>
          <button className="btn-primary btn-sm" onClick={() => setImportOpen(true)} data-testid="inv-import-btn">
            <Upload size={13} /> Import CSV
          </button>
        </div>
      </div>

      {err && <div className="rounded-lg border border-[#ff3366]/40 bg-[#ff3366]/5 px-4 py-3 text-sm text-[#ff3366]">{err}</div>}

      {/* Low-stock alerts */}
      {lowStockLines.length > 0 && (
        <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 px-5 py-4 flex gap-3 items-start" data-testid="low-stock-banner">
          <AlertTriangle size={18} className="text-amber-400 mt-0.5 shrink-0" />
          <div>
            <div className="font-display text-sm text-amber-300 uppercase tracking-widest">Low Inventory</div>
            <div className="text-sm text-zinc-300 mt-1">
              These <span className="text-white">active</span> products have <span className="text-white">0 available</span> pool accounts. New buyers will land in <code className="text-xs">account_pending</code> until you top up:
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
              {lowStockLines.map(l => (
                <span key={l} className="px-2 py-1 rounded-md bg-amber-500/10 border border-amber-500/30 text-[11px] font-mono text-amber-300">
                  {l}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Inventory matrix */}
      <div className="card-tech rounded-lg overflow-x-auto">
        <div className="min-w-[900px]">
          <div className="grid grid-cols-[1.6fr_repeat(6,1fr)_1.1fr] gap-2 px-4 py-3 label-xs border-b border-white/5">
            <div>PRODUCT · SIZE</div>
            {STATUSES.map(s => <div key={s} className="text-right">{s}</div>)}
            <div className="text-right">TOTAL</div>
          </div>
          {PRODUCT_TYPES.flatMap(pt => SIZES.map(sz => {
            const total = rowTotal(pt, sz);
            const lowStock = isLowStock(pt, sz);
            const p = products.find(x => x.product_type === pt && x.account_size === sz);
            return (
              <div
                key={`${pt}-${sz}`}
                className={`grid grid-cols-[1.6fr_repeat(6,1fr)_1.1fr] gap-2 px-4 py-3 text-sm border-b border-white/5 items-center ${lowStock ? 'bg-amber-500/[0.03]' : ''}`}
                data-testid={`inv-row-${pt}-${sz}`}
              >
                <div className="flex items-center gap-2">
                  <span className="font-mono">{TYPE_LABEL[pt]}</span>
                  <span className="font-mono text-zinc-500">·</span>
                  <span className="font-mono text-white">${sz.toLocaleString()}</span>
                  {p?.active && <span className="chip chip-green text-[9px]">LIVE</span>}
                  {lowStock && <AlertTriangle size={12} className="text-amber-400" />}
                </div>
                {STATUSES.map(st => {
                  const n = cell(pt, sz, st);
                  return (
                    <div key={st} className={`text-right font-mono ${STATUS_TINT[st]} ${n === 0 ? 'opacity-30' : ''}`}
                         data-testid={`inv-cell-${pt}-${sz}-${st}`}>
                      {n}
                    </div>
                  );
                })}
                <div className="text-right font-mono text-zinc-400">{total}</div>
              </div>
            );
          }))}
        </div>
      </div>

      {/* Pool row table */}
      <div className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="font-display text-2xl mr-2">Pool accounts</h2>
          <select className="input-tech" style={{ maxWidth: 150 }} value={filter.product_type}
                  onChange={e => setFilter({ ...filter, product_type: e.target.value })}
                  data-testid="inv-filter-type">
            <option value="">All types</option>
            {PRODUCT_TYPES.map(t => <option key={t} value={t}>{TYPE_LABEL[t]}</option>)}
          </select>
          <select className="input-tech" style={{ maxWidth: 150 }} value={filter.account_size}
                  onChange={e => setFilter({ ...filter, account_size: e.target.value })}
                  data-testid="inv-filter-size">
            <option value="">All sizes</option>
            {SIZES.map(s => <option key={s} value={s}>${s.toLocaleString()}</option>)}
          </select>
          <select className="input-tech" style={{ maxWidth: 160 }} value={filter.status}
                  onChange={e => setFilter({ ...filter, status: e.target.value })}
                  data-testid="inv-filter-status">
            <option value="">All statuses</option>
            {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <div className="relative flex-1 min-w-[200px]">
            <Search size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <input className="input-tech pl-8" placeholder="Search mt_login / server"
                   value={q} onChange={e => setQ(e.target.value)} data-testid="inv-search" />
          </div>
        </div>

        <div className="card-tech rounded-lg overflow-x-auto">
          <div className="min-w-[900px]">
            <div className="grid grid-cols-[1fr_1fr_1.3fr_1.3fr_1fr_1.4fr] gap-3 px-4 py-3 label-xs border-b border-white/5">
              <div>TYPE · SIZE</div><div>STATUS</div><div>MT LOGIN</div><div>SERVER</div><div>ASSIGNED TO</div><div className="text-right">ACTIONS</div>
            </div>
            {filtered.length === 0 && (
              <div className="px-4 py-10 text-center text-sm text-zinc-500" data-testid="inv-empty">
                No pool rows match the current filter.
              </div>
            )}
            {filtered.slice(0, 500).map(r => (
              <div key={r.pool_id}
                   className="grid grid-cols-[1fr_1fr_1.3fr_1.3fr_1fr_1.4fr] gap-3 px-4 py-3 border-b border-white/5 text-sm items-center"
                   data-testid={`pool-row-${r.pool_id}`}>
                <div className="font-mono text-xs">{TYPE_LABEL[r.product_type]} · ${Number(r.account_size).toLocaleString()}</div>
                <div><span className={`chip ${r.status === 'AVAILABLE' ? 'chip-green' : r.status === 'RETIRED' || r.status === 'BLOCKED' ? 'chip-red' : 'chip-yellow'}`}>{r.status}</span></div>
                <div className="font-mono text-xs truncate">{r.mt_login}</div>
                <div className="font-mono text-xs truncate">{r.mt_server}</div>
                <div className="font-mono text-xs text-zinc-500 truncate">{r.assigned_trader_id || '—'}</div>
                <div className="text-right">
                  {r.status !== 'ACTIVE' && r.status !== 'RETIRED' && (
                    <button className="btn-ghost btn-sm text-[#ff3366]" onClick={() => retire(r.pool_id)}
                            disabled={busy} data-testid={`retire-${r.pool_id}`}>
                      <Trash2 size={11} className="inline mr-1" /> Retire
                    </button>
                  )}
                </div>
              </div>
            ))}
            {filtered.length > 500 && (
              <div className="px-4 py-2 text-xs text-zinc-500">
                Showing 500 of {filtered.length} — refine your filter to see more.
              </div>
            )}
          </div>
        </div>
      </div>

      {importOpen && <CsvImportModal onClose={() => setImportOpen(false)} onDone={load} />}
    </div>
  );
}

// ---------- CSV import modal ----------
function CsvImportModal({ onClose, onDone }) {
  const [text, setText] = useState('');
  const [parsed, setParsed] = useState(null);   // { valid: [], invalid: [] }
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);   // { inserted, skipped }
  const [err, setErr] = useState('');

  const onFile = async (f) => {
    if (!f) return;
    const t = await f.text();
    setText(t);
    setParsed(parseCsv(t));
    setResult(null);
    setErr('');
  };

  const onTextChange = (t) => {
    setText(t);
    setParsed(t.trim() ? parseCsv(t) : null);
    setResult(null);
  };

  const submit = async () => {
    if (!parsed || parsed.valid.length === 0) return;
    setBusy(true); setErr('');
    try {
      const res = await adminAPI.poolBulkAdd(parsed.valid);
      setResult(res);
      await onDone();
    } catch (e) { setErr(fmtError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-md flex items-center justify-center p-4"
         onClick={onClose} data-testid="csv-import-modal">
      <div className="card-tech rounded-2xl p-6 max-w-3xl w-full max-h-[92vh] overflow-y-auto"
           onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="label-xs">POOL BULK IMPORT</div>
            <h3 className="font-display text-2xl mt-1">Import MT5 accounts from CSV</h3>
          </div>
          <button className="btn-ghost btn-sm" onClick={onClose}><XCircle size={14} /></button>
        </div>
        <div className="text-xs text-zinc-400 mt-4 leading-relaxed">
          Required columns: <code className="text-white">{REQUIRED_COLS.join(', ')}</code>
          <br />Optional: <code className="text-zinc-300">{OPTIONAL_COLS.join(', ')}</code>
          <br />Header row required. <span className="text-zinc-500">product_type ∈ [one_step, instant, funded] · account_size ∈ [5000, 10000, 20000]</span>
        </div>

        <div className="mt-4 space-y-3">
          <label className="block">
            <input type="file" accept=".csv,text/csv" onChange={e => onFile(e.target.files?.[0])}
                   className="hidden" id="csv-file-input" data-testid="csv-file-input" />
            <span className="btn-ghost btn-sm inline-flex cursor-pointer" onClick={() => document.getElementById('csv-file-input').click()}>
              <FileText size={12} className="inline mr-1" /> Choose CSV file
            </span>
          </label>
          <textarea
            className="input-tech font-mono text-xs"
            rows={8}
            placeholder="product_type,account_size,mt_login,mt_password,mt_server&#10;one_step,5000,74000123,Passw0rd!,Deriv-Demo"
            value={text}
            onChange={e => onTextChange(e.target.value)}
            data-testid="csv-textarea"
          />
        </div>

        {parsed && (
          <div className="mt-4 grid grid-cols-2 gap-3">
            <div className="rounded-lg border border-[#00ff66]/30 bg-[#00ff66]/5 px-4 py-3">
              <div className="label-xs text-[#00ff66]">VALID</div>
              <div className="font-display text-2xl mt-1 text-[#00ff66]" data-testid="csv-valid-count">{parsed.valid.length}</div>
            </div>
            <div className={`rounded-lg border ${parsed.invalid.length ? 'border-[#ff3366]/30 bg-[#ff3366]/5' : 'border-white/10'} px-4 py-3`}>
              <div className={`label-xs ${parsed.invalid.length ? 'text-[#ff3366]' : ''}`}>INVALID</div>
              <div className={`font-display text-2xl mt-1 ${parsed.invalid.length ? 'text-[#ff3366]' : 'text-zinc-500'}`} data-testid="csv-invalid-count">
                {parsed.invalid.length}
              </div>
            </div>
          </div>
        )}

        {parsed && parsed.invalid.length > 0 && (
          <div className="mt-3 max-h-32 overflow-y-auto rounded-lg border border-white/5 bg-black/40 p-2 text-xs font-mono">
            {parsed.invalid.slice(0, 20).map((row, i) => (
              <div key={i} className="text-[#ff3366]">Line {row.line}: {row.error}</div>
            ))}
          </div>
        )}

        {result && (
          <div className="mt-4 rounded-lg border border-[#00ff66]/30 bg-[#00ff66]/5 px-4 py-3 flex items-center gap-2">
            <CheckCircle2 size={14} className="text-[#00ff66]" />
            <div className="text-sm">
              Inserted <span className="text-[#00ff66] font-mono">{result.inserted}</span> · skipped <span className="text-zinc-400 font-mono">{result.skipped.length}</span>.
              {result.skipped.length > 0 && (
                <span className="text-zinc-500 text-xs ml-2">(likely duplicates of existing mt_login)</span>
              )}
            </div>
          </div>
        )}

        {err && <div className="mt-3 text-sm text-[#ff3366]">{err}</div>}

        <div className="mt-6 flex gap-2 justify-end">
          <button className="btn-ghost" onClick={onClose} disabled={busy}>Close</button>
          <button className="btn-primary" onClick={submit}
                  disabled={busy || !parsed || parsed.valid.length === 0}
                  data-testid="csv-import-submit">
            {busy ? 'Importing…' : parsed ? `Import ${parsed.valid.length} accounts` : 'Import'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------- Tiny CSV parser ----------
function parseCsv(text) {
  const lines = text.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
  if (lines.length < 2) return { valid: [], invalid: [] };
  const cols = lines[0].split(',').map(c => c.trim().toLowerCase());
  const idx = Object.fromEntries(cols.map((c, i) => [c, i]));
  const missing = REQUIRED_COLS.filter(c => !(c in idx));
  if (missing.length) return { valid: [], invalid: [{ line: 1, error: `Missing columns: ${missing.join(', ')}` }] };
  const valid = [];
  const invalid = [];
  const seen = new Set();
  for (let i = 1; i < lines.length; i++) {
    const parts = lines[i].split(',').map(c => c.trim());
    const row = {
      product_type: parts[idx.product_type],
      account_size: parseInt(parts[idx.account_size], 10),
      mt_login: parts[idx.mt_login],
      mt_password: parts[idx.mt_password],
      mt_server: parts[idx.mt_server],
      mt_investor_password: idx.mt_investor_password != null ? parts[idx.mt_investor_password] : '',
      mt_broker: idx.mt_broker != null ? parts[idx.mt_broker] : 'Deriv',
    };
    if (!['one_step', 'instant', 'funded'].includes(row.product_type)) {
      invalid.push({ line: i + 1, error: `bad product_type: ${row.product_type}` }); continue;
    }
    if (![5000, 10000, 20000].includes(row.account_size)) {
      invalid.push({ line: i + 1, error: `bad account_size: ${row.account_size}` }); continue;
    }
    if (!row.mt_login || !row.mt_password || !row.mt_server) {
      invalid.push({ line: i + 1, error: 'missing mt_login / mt_password / mt_server' }); continue;
    }
    if (seen.has(row.mt_login)) {
      invalid.push({ line: i + 1, error: `duplicate mt_login in CSV: ${row.mt_login}` }); continue;
    }
    seen.add(row.mt_login);
    valid.push(row);
  }
  return { valid, invalid };
}
